import { compact } from "lodash";
import { useMemo } from "react";
import { AssessmentType } from "types/graphql-schema";

import { getProgramUrl } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertNonNull } from "@helpers/helpers";

import ManagerEffectivenessChart from "./assessment-report/manager-effectiveness-chart";
import ManagerEffectivenessTable from "./assessment-report/manager-effectiveness-table";
import {
  getFilterByType,
  matchesUpwardAssessmentFilter,
} from "./assessment-report/report-filters/helpers";
import { AssessmentReportFilterType } from "./assessment-report/report-filters/types";
import useAssessmentReport from "./assessment-report/use-assessment-report";

const ManagerEffectivenessReport = ({
  complianceProgramId,
  isInReporting,
  headerContent,
  embedded = false,
}: {
  complianceProgramId: number;
  isInReporting: boolean;
  headerContent: (reportSettings: JSX.Element) => JSX.Element;
  // When embedded in the Reviews page (no Reporting nav bar above), tighten the
  // top spacing: add padding above the header row and shrink the body offset.
  embedded?: boolean;
}) => {
  const label = useLabel();
  const {
    viewOptions,
    showViewOptionsSidebar,
    handleHideViewOptionsSidebar,
    handleShowViewOptionsSidebar,
    handleUpdateViewOptions,
    handleResetViewOptionsToBaseline,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    complianceProgram,
    isLoadingProgram,
    assessmentTemplate,
    questions,
    talentIndicatorQuestions,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
    savedViewOptions,
    handleChangeSavedView,
  } = useAssessmentReport(AssessmentType.Manager, complianceProgramId);

  const rowNodes = useMemo(
    () =>
      complianceProgram?.managerEffectivenessReport
        ? assertNonNull(complianceProgram.managerEffectivenessReport).edges.map(
            (rowEdge) => {
              return assertNonNull(rowEdge?.node);
            },
          )
        : [],
    [complianceProgram],
  );

  const filteredTableRows = useMemo(() => {
    const filterItems = viewOptions.filters.list;
    const filterMatchType = viewOptions.filters.type;
    const userFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.USER,
    );
    const reportsToFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.REPORTS_TO,
    );
    const teamFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.TEAM,
    );
    return rowNodes.filter((row) =>
      matchesUpwardAssessmentFilter({
        userFilterList,
        reportsToFilterList,
        teamFilterList,
        target: row.manager,
        matchType: filterMatchType,
      }),
    );
  }, [viewOptions, rowNodes]);

  const reportDropdownActions = useMemo(() => {
    const isWorking = isSavingReportingView || isDeletingReportingView;
    return removeConsecutiveDividers(
      compact([
        viewOptions.id && {
          label: "Duplicate this view",
          onClick: handleDuplicateView,
          disabled: isWorking,
        },
        viewOptions.id &&
          viewOptions.isDirty && {
            label: "Save as new view",
            onClick: handleSaveAsNewView,
            disabled: isWorking,
          },
        viewOptions.id && {
          label: "Delete this view",
          onClick: handleDeleteView,
          disabled: isWorking,
        },
        {
          type: "divider",
        },
        isInReporting && {
          type: "link",
          label: `View ${label("review", { capitalize: true })} Progress`,
          to: getProgramUrl(complianceProgramId, { subPath: "/progress" }),
          disabled: isWorking,
        },
      ]),
    );
  }, [
    complianceProgramId,
    handleDeleteView,
    handleDuplicateView,
    handleSaveAsNewView,
    isDeletingReportingView,
    isInReporting,
    isSavingReportingView,
    label,
    viewOptions,
  ]);

  const filterItems = viewOptions.filters.list;

  return (
    <div>
      {isLoadingProgram ? (
        <div className="flex-1 flex justify-center p-10">
          <Loading>Loading...</Loading>
        </div>
      ) : (
        <>
          <ReportingViewDeletionConfirm />
          <div
            className={`px-8 pb-4 z-30 bg-content-background sticky top-0 ${
              embedded ? "pt-6" : ""
            }`}
          >
            {headerContent(
              <div className="flex-1 flex items-center justify-between">
                <ComboboxGeneric
                  className="max-w-[180px]"
                  options={savedViewOptions}
                  disabled={isSavingReportingView || isDeletingReportingView}
                  onChangeValue={handleChangeSavedView}
                  value={
                    savedViewOptions.find(
                      ({ value }) => value === viewOptions.id,
                    ) ?? null
                  }
                  placeholder={
                    savedViewOptions.length > 0
                      ? "Select a view..."
                      : "No saved views"
                  }
                />
                <div className="flex items-center gap-4">
                  <Button
                    disabled={isSavingReportingView || isDeletingReportingView}
                    className="flex items-center ml-4"
                    theme={buttonTheme.iconGray}
                    onClick={handleShowViewOptionsSidebar}
                  >
                    <TopicflowIcon type="adjustments" className="h-5 w-5" />
                  </Button>
                  {viewOptions.isDirty && (
                    <>
                      <Button
                        size={ButtonSize.large}
                        theme={buttonTheme.text}
                        disabled={
                          isSavingReportingView || isDeletingReportingView
                        }
                        text="Reset"
                        onClick={handleResetViewOptionsToBaseline}
                      />
                      <Button
                        disabled={
                          isSavingReportingView || isDeletingReportingView
                        }
                        size={ButtonSize.large}
                        theme={buttonTheme.primary}
                        className="flex items-center gap-1"
                        onClick={handleSaveExistingView}
                        text="Save View"
                      />
                    </>
                  )}
                  <Dropdown
                    align="end"
                    disabled={reportDropdownActions.length === 0}
                    options={reportDropdownActions}
                  />
                </div>
              </div>,
            )}
          </div>
          <div
            className={`absolute inset-0 overflow-y-scroll px-8 mb-12 ${
              embedded ? "mt-20" : "mt-32"
            }`}
          >
            {complianceProgram && (
              <ManagerEffectivenessChart
                questions={questions}
                complianceProgram={complianceProgram}
                filterMatchType={viewOptions.filters.type}
                userFilterList={getFilterByType(
                  filterItems,
                  AssessmentReportFilterType.USER,
                )}
                reportsToFilterList={getFilterByType(
                  filterItems,
                  AssessmentReportFilterType.REPORTS_TO,
                )}
                teamFilterList={getFilterByType(
                  filterItems,
                  AssessmentReportFilterType.TEAM,
                )}
              />
            )}

            {complianceProgram && assessmentTemplate && (
              <ManagerEffectivenessTable
                questions={questions}
                complianceProgram={complianceProgram}
                viewOptions={viewOptions}
                filteredTableRows={filteredTableRows}
                showViewOptionsSidebar={!!showViewOptionsSidebar}
                talentIndicatorQuestions={talentIndicatorQuestions}
                onUpdateViewOptions={handleUpdateViewOptions}
                onCloseViewOptionsSidebar={handleHideViewOptionsSidebar}
                onResetViewToBaseline={handleResetViewOptionsToBaseline}
                onSaveView={handleSaveExistingView}
                onSaveAsNewView={handleSaveAsNewView}
                onDuplicateView={handleDuplicateView}
                onDeleteView={handleDeleteView}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManagerEffectivenessReport;
